import { useStorage } from '@vivli/shared/infrastructure/hook';
import { LoggerService } from '@vivli/shared/infrastructure/service';
import { acquireTabLock, hasLivePeer, releaseTab } from './tab-liveness';

export const useLogoutOnClose = () => {
    const { getItem, setItem, deleteItem } = useStorage();

    // Per-tab flag: set once this tab has established (or adopted) a session. Survives the
    // same-tab B2C redirect round-trip because sessionStorage persists across same-tab navigation.
    const sessionActiveKey = `session_active`;

    // Mark this tab as owning a live session and start advertising its liveness to other tabs.
    const markSessionActive = () => {
        LoggerService.debug('[Auth:Session] markSessionActive: claiming session ownership for this tab and acquiring the tab lock');
        setItem(sessionActiveKey, true, 'session');
        acquireTabLock();
    };

    // End this tab's session ownership (called on explicit logout).
    const endSession = () => {
        LoggerService.debug('[Auth:Session] endSession: releasing session ownership and the tab lock');
        deleteItem(sessionActiveKey, 'session');
        releaseTab();
    };

    // Decide whether a freshly loaded tab should be logged out.
    //   1. This tab already owns a session      -> keep (no logout)
    //   2. Another tab is alive                  -> adopt its session (no logout)
    //   3. No session owner anywhere             -> fresh browser session -> logout
    const checkIfLogoutNeeded = async (): Promise<boolean> => {
        const isSessionActive = getItem<boolean>(sessionActiveKey, 'session');
        LoggerService.debug(`[Auth:Session] checkIfLogoutNeeded: session_active = ${isSessionActive}`);
        if (isSessionActive) {
            LoggerService.debug('[Auth:Session] checkIfLogoutNeeded: this tab already owns the session -> keep (no logout)');
            return false;
        }

        const peerAlive = await hasLivePeer();
        LoggerService.debug(
            peerAlive
                ? '[Auth:Session] checkIfLogoutNeeded: another tab is alive -> adopt its session (no logout)'
                : '[Auth:Session] checkIfLogoutNeeded: no live peer and no owned session -> fresh browser session -> LOGOUT'
        );
        return !peerAlive;
    };

    return {
        markSessionActive,
        checkIfLogoutNeeded,
        endSession
    };
};
